import React, { useState, useEffect } from 'react';
import { classAPI, activityAPI, walletAPI, groupAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ProfilePhoto } from '../ProfilePhoto';
import StudentProfileDialog from './StudentProfileDialog';
import { stripHtml } from '../common/RichTextEditor';
import GroupChatDialog from './GroupChatDialog';
import DirectChatDialog from './DirectChatDialog';
import GroupDetailDialog from './GroupDetailDialog';
import GroupFormModal from './GroupFormModal';
import GroupMembersModal from './GroupMembersModal';
import GroupAwardModal from './GroupAwardModal';
import AssistantsManagerDialog from './AssistantsManagerDialog';
import { Coins, Award, Search, UserPlus, Minus, Plus, Zap, Star, Eye, Users, UsersRound, Pencil, Trash2, MessageCircle, MessageSquare, Crown, Wallet, ChevronDown, ChevronUp, History, UserMinus, ShieldCheck, ShieldPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins } from '../../lib/utils';

export default function StudentsTab({ classId, activities, onRefresh, openGroupChatId, onGroupChatOpened, isOwner = false, className = '' }) {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [assistantsDialogOpen, setAssistantsDialogOpen] = useState(false);
  const [promotingId, setPromotingId] = useState(null);

  // Student award/adjust
  const [awardOpen, setAwardOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [awardNote, setAwardNote] = useState('');
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustNote, setAdjustNote] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileStudent, setProfileStudent] = useState(null);
  
  // Group management
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupFormData, setGroupFormData] = useState({ name: '', description: '', color: '#3B82F6', leader_user_id: '' });
  const [selectedStudentsForGroup, setSelectedStudentsForGroup] = useState([]);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [membersToManage, setMembersToManage] = useState([]);
  const [awardGroupDialogOpen, setAwardGroupDialogOpen] = useState(false);
  const [groupAwardActivity, setGroupAwardActivity] = useState('');
  const [groupAwardNote, setGroupAwardNote] = useState('');
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [chatGroup, setChatGroup] = useState(null);
  const [directChatOpen, setDirectChatOpen] = useState(false);
  const [directChatStudent, setDirectChatStudent] = useState(null);
  const [groupDetailOpen, setGroupDetailOpen] = useState(false);
  const [groupDetailId, setGroupDetailId] = useState(null);

  useEffect(() => { loadData(); }, [classId]);

  useEffect(() => {
    if (openGroupChatId && groups.length > 0) {
      const group = groups.find(g => g.id === openGroupChatId);
      if (group) { setChatGroup(group); setChatDialogOpen(true); onGroupChatOpened?.(); }
    }
  }, [openGroupChatId, groups]);

  const loadData = async () => {
    try {
      const [studentsRes, groupsRes, assistantsRes] = await Promise.all([
        classAPI.getStudents(classId),
        groupAPI.list(classId, true),
        classAPI.listAssistants(classId).catch(() => ({ data: [] })),
      ]);
      setStudents(studentsRes.data || []);
      setGroups(groupsRes.data || []);
      setAssistants(assistantsRes.data || []);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const assistantIdSet = new Set((assistants || []).map(a => a.user_id));

  const handlePromoteToAssistant = async (student) => {
    if (!isOwner) return;
    if (!window.confirm(`¿Convertir a ${student.user_name} en asistente de la clase? Podrá ver estudiantes, aprobar entregas y gestionar la clase como tú.`)) return;
    setPromotingId(student.user_id);
    try {
      await classAPI.addAssistant(classId, { user_id: student.user_id });
      toast.success(`${student.user_name} ahora es asistente`);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo promover a asistente');
    } finally {
      setPromotingId(null);
    }
  };

  const activeActivities = activities?.filter(a => a.is_active) || [];
  const filteredStudents = students.filter(s =>
    s.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ─── Student handlers ───
  const handleAwardCoins = async () => {
    if (!selectedActivity) { toast.error('Selecciona una actividad'); return; }
    setSubmitting(true);
    try {
      await activityAPI.award(classId, selectedActivity, { student_user_id: selectedStudent.user_id, note: awardNote });
      toast.success(`Coins y XP otorgados a ${selectedStudent.user_name}`);
      setAwardOpen(false); setSelectedActivity(''); setAwardNote('');
      loadData(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al otorgar coins'); }
    finally { setSubmitting(false); }
  };

  const handleAdjustBalance = async () => {
    if (adjustAmount === 0) { toast.error('El ajuste debe ser diferente de 0'); return; }
    if (!adjustNote.trim()) { toast.error('Debes incluir una nota explicativa'); return; }
    setSubmitting(true);
    try {
      await walletAPI.adjust(classId, { student_user_id: selectedStudent.user_id, amount: adjustAmount, note: adjustNote });
      toast.success(`Balance ajustado para ${selectedStudent.user_name}`);
      setAdjustOpen(false); setAdjustAmount(0); setAdjustNote('');
      loadData(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al ajustar balance'); }
    finally { setSubmitting(false); }
  };

  const handleRemoveStudent = async (student) => {
    if (!window.confirm(`¿Eliminar a ${student.user_name} de esta clase? Se borrarán todos sus datos en esta clase.`)) return;
    try {
      await classAPI.removeStudent(classId, student.user_id);
      toast.success(`${student.user_name} eliminado de la clase`);
      loadData(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar estudiante'); }
  };

  // ─── Group handlers ───
  const resetGroupForm = () => { setGroupFormData({ name: '', description: '', color: '#3B82F6', leader_user_id: '' }); setEditingGroup(null); setSelectedStudentsForGroup([]); };

  const handleOpenCreateGroup = () => { resetGroupForm(); setGroupDialogOpen(true); };

  const handleOpenEditGroup = (group, e) => {
    e?.stopPropagation();
    setEditingGroup(group);
    setGroupFormData({ name: group.name, description: group.description || '', color: group.color || '#3B82F6', leader_user_id: group.leader_user_id || '' });
    setSelectedStudentsForGroup(group.members?.map(m => m.user_id) || []);
    setGroupDialogOpen(true);
  };

  const handleSubmitGroup = async (e) => {
    e.preventDefault();
    if (!groupFormData.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSubmitting(true);
    try {
      if (editingGroup) {
        await groupAPI.update(classId, editingGroup.id, { name: groupFormData.name, description: groupFormData.description, color: groupFormData.color, leader_user_id: groupFormData.leader_user_id || null });
        const currentMembers = editingGroup.members?.map(m => m.user_id) || [];
        const toAdd = selectedStudentsForGroup.filter(id => !currentMembers.includes(id));
        const toRemove = currentMembers.filter(id => !selectedStudentsForGroup.includes(id));
        if (toAdd.length > 0) await groupAPI.addMembers(classId, editingGroup.id, toAdd);
        if (toRemove.length > 0) await groupAPI.removeMembers(classId, editingGroup.id, toRemove);
        toast.success('Grupo actualizado');
      } else {
        const createRes = await groupAPI.create(classId, { name: groupFormData.name, description: groupFormData.description, color: groupFormData.color, leader_user_id: groupFormData.leader_user_id || null });
        const newGroupId = createRes.data.id;
        if (selectedStudentsForGroup.length > 0) {
          await groupAPI.addMembers(classId, newGroupId, selectedStudentsForGroup);
          if (groupFormData.leader_user_id && selectedStudentsForGroup.includes(groupFormData.leader_user_id)) {
            await groupAPI.setLeader(classId, newGroupId, groupFormData.leader_user_id);
          }
        }
        toast.success(`Grupo creado con ${selectedStudentsForGroup.length} miembros`);
      }
      setGroupDialogOpen(false); resetGroupForm(); loadData(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar grupo'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteGroup = async (groupId, e) => {
    e?.stopPropagation();
    if (!window.confirm('¿Eliminar este grupo?')) return;
    try { await groupAPI.delete(classId, groupId); toast.success('Grupo eliminado'); if (selectedGroup?.id === groupId) setSelectedGroup(null); loadData(); }
    catch { toast.error('Error al eliminar grupo'); }
  };

  const handleOpenManageMembers = (group, e) => {
    e?.stopPropagation();
    setSelectedGroup(group); setMembersToManage(group.members?.map(m => m.user_id) || []); setMemberDialogOpen(true);
  };

  const handleSaveMembers = async () => {
    if (!selectedGroup) return;
    setSubmitting(true);
    try {
      const currentMembers = selectedGroup.members?.map(m => m.user_id) || [];
      const toAdd = membersToManage.filter(id => !currentMembers.includes(id));
      const toRemove = currentMembers.filter(id => !membersToManage.includes(id));
      if (toAdd.length > 0) await groupAPI.addMembers(classId, selectedGroup.id, toAdd);
      if (toRemove.length > 0) await groupAPI.removeMembers(classId, selectedGroup.id, toRemove);
      toast.success('Miembros actualizados'); setMemberDialogOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar miembros'); }
    finally { setSubmitting(false); }
  };

  const handleOpenAwardGroup = (group, e) => {
    e?.stopPropagation(); setSelectedGroup(group); setGroupAwardActivity(''); setGroupAwardNote(''); setAwardGroupDialogOpen(true);
  };

  const handleOpenChat = (group, e) => { e?.stopPropagation(); setChatGroup(group); setChatDialogOpen(true); };

  const handleAwardGroup = async () => {
    if (!groupAwardActivity || !selectedGroup) { toast.error('Selecciona una actividad'); return; }
    setSubmitting(true);
    try {
      const res = await groupAPI.awardActivity(classId, groupAwardActivity, selectedGroup.id, groupAwardNote);
      toast.success(res.data.message); setAwardGroupDialogOpen(false); loadData(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al otorgar coins'); }
    finally { setSubmitting(false); }
  };

  const toggleStudentForGroup = (action) => {
    if (action === 'clear_all') { setSelectedStudentsForGroup([]); setGroupFormData(gf => ({...gf, leader_user_id: ''})); return; }
    if (action === 'select_all') { setSelectedStudentsForGroup(students.map(s => s.user_id)); return; }
    setSelectedStudentsForGroup(prev => {
      const isSelected = prev.includes(action);
      const next = isSelected ? prev.filter(id => id !== action) : [...prev, action];
      if (isSelected && groupFormData.leader_user_id === action) {
        setTimeout(() => setGroupFormData(gf => ({...gf, leader_user_id: ''})), 0);
      }
      return next;
    });
  };

  const toggleMemberSelection = (action) => {
    if (action === 'clear_all') { setMembersToManage([]); return; }
    if (action === 'select_all') { setMembersToManage(students.map(s => s.user_id)); return; }
    setMembersToManage(prev => prev.includes(action) ? prev.filter(id => id !== action) : [...prev, action]);
  };

  // ─── Render ───
  return (
    <div className="space-y-6">
      <Tabs defaultValue="students" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="students" className="flex items-center gap-2" data-testid="tab-students-list">
            <Users className="w-4 h-4" /> Estudiantes ({students.length})
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2" data-testid="tab-groups-list">
            <UsersRound className="w-4 h-4" /> Grupos ({groups.length})
          </TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students">
          {/* Assistants management card (only owner sees the manage button) */}
          <Card className="border-slate-200 mb-4" data-testid="assistants-card">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Asistentes de la Clase ({assistants.length})
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {isOwner
                      ? 'Invita a otro profesor o a un estudiante de esta clase como asistente'
                      : 'Personas con permisos de gestión en esta clase'}
                  </CardDescription>
                </div>
                {isOwner && (
                  <Button
                    size="sm"
                    onClick={() => setAssistantsDialogOpen(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                    data-testid="open-assistants-manager"
                  >
                    <ShieldPlus className="w-4 h-4 mr-1.5" />
                    Gestionar Asistentes
                  </Button>
                )}
              </div>
            </CardHeader>
            {assistants.length > 0 && (
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2">
                  {assistants.map((a) => (
                    <div
                      key={a.user_id}
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full"
                      data-testid={`assistant-chip-${a.user_id}`}
                    >
                      <ProfilePhoto photoUrl={a.photo_url} name={a.name} size={22} />
                      <span className="text-xs font-medium text-emerald-900">{a.name}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                        a.role === 'professor' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {a.role === 'professor' ? 'PROF.' : 'EST.'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-lg sm:text-xl">Estudiantes</CardTitle>
                  <CardDescription>{students.length} estudiantes inscritos</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Buscar estudiante..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="search-students" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
              ) : students.length === 0 ? (
                <div className="text-center py-12">
                  <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay estudiantes inscritos</p>
                  <p className="text-sm text-slate-400">Comparte el código de clase para que se unan</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Estudiante</TableHead>
                        <TableHead className="hidden sm:table-cell">ID</TableHead>
                        <TableHead className="text-center">Nivel</TableHead>
                        <TableHead className="text-center hidden sm:table-cell">XP</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStudents.map((student) => (
                        <TableRow key={student.id} data-testid={`student-row-${student.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80" onClick={() => { setProfileStudent(student); setProfileOpen(true); }}>
                              <ProfilePhoto photoUrl={student.photo_url} name={student.user_name} size={36} />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-slate-900 truncate hover:text-blue-600">{student.user_name}</p>
                                <p className="text-xs text-slate-500 truncate hidden sm:block">{student.user_email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600 hidden sm:table-cell">{student.student_id || '-'}</TableCell>
                          <TableCell className="text-center">
                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full font-bold text-xs sm:text-sm">
                              <Star className="w-3 h-3" />{student.level || 1}
                            </div>
                          </TableCell>
                          <TableCell className="text-center hidden sm:table-cell">
                            <div className="inline-flex items-center gap-1 text-purple-600 font-medium">
                              <Zap className="w-4 h-4" />{student.total_xp || 0}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 bg-amber-50 text-amber-700 rounded-full font-bold text-xs sm:text-sm">
                              <Coins className="w-3 h-3 sm:w-4 sm:h-4" />{formatCoins(student.balance || 0)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 sm:gap-2">
                              <Button size="sm" variant="outline" className="h-8 px-2 sm:px-3 text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => { setDirectChatStudent({ id: student.user_id, name: student.user_name, photo_url: student.photo_url, role: 'student' }); setDirectChatOpen(true); }} data-testid={`message-btn-${student.user_id}`}>
                                <MessageSquare className="w-4 h-4" /><span className="hidden sm:inline ml-1">Mensaje</span>
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 px-2 sm:px-3" onClick={() => { setProfileStudent(student); setProfileOpen(true); }} data-testid={`view-profile-${student.user_id}`}>
                                <Eye className="w-4 h-4" /><span className="hidden sm:inline ml-1">Ver</span>
                              </Button>
                              <Dialog open={awardOpen && selectedStudent?.user_id === student.user_id} onOpenChange={(open) => { setAwardOpen(open); if (open) setSelectedStudent(student); }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2 sm:px-3" data-testid={`award-btn-${student.user_id}`}>
                                    <Award className="w-4 h-4" /><span className="hidden sm:inline ml-1">Otorgar</span>
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle className="font-heading">Otorgar Recompensa</DialogTitle>
                                    <DialogDescription>Otorgar coins y XP a {student.user_name}</DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                      <ProfilePhoto photoUrl={student.photo_url} name={student.user_name} size={40} />
                                      <div>
                                        <p className="font-medium text-sm">{student.user_name}</p>
                                        <div className="flex items-center gap-3 mt-1">
                                          <span className="text-xs text-amber-600 font-medium"><Coins className="w-3 h-3 inline mr-1" />{student.balance} coins</span>
                                          <span className="text-xs text-purple-600 font-medium"><Zap className="w-3 h-3 inline mr-1" />{student.total_xp} XP</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Actividad Completada</Label>
                                      <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                                        <SelectTrigger data-testid="select-activity"><SelectValue placeholder="Selecciona una actividad" /></SelectTrigger>
                                        <SelectContent>
                                          {activeActivities.map((a) => (
                                            <SelectItem key={a.id} value={a.id}>
                                              <div className="flex items-center gap-2">
                                                <span>{a.title}</span>
                                                <Badge className="bg-amber-50 text-amber-700">{a.coin_value} coins</Badge>
                                                {a.xp_value > 0 && <Badge className="bg-purple-50 text-purple-700">+{a.xp_value} XP</Badge>}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Nota (opcional)</Label>
                                      <Textarea placeholder="Comentario adicional..." value={awardNote} onChange={(e) => setAwardNote(e.target.value)} data-testid="award-note" />
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button onClick={handleAwardCoins} disabled={submitting} data-testid="confirm-award">
                                      {submitting ? 'Otorgando...' : 'Otorgar Recompensa'}
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                              <Dialog open={adjustOpen && selectedStudent?.user_id === student.user_id} onOpenChange={(open) => { setAdjustOpen(open); if (open) setSelectedStudent(student); }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline" className="h-8 px-2 sm:px-3" data-testid={`adjust-btn-${student.user_id}`}>
                                    <span className="hidden sm:inline">Ajustar</span><span className="sm:hidden"><Minus className="w-4 h-4" /></span>
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle className="font-heading">Ajustar Balance</DialogTitle>
                                    <DialogDescription>Ajustar manualmente el balance de {student.user_name}</DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div className="text-center">
                                      <p className="text-sm text-slate-500 mb-2">Balance Actual</p>
                                      <div className="inline-flex items-center gap-2 text-2xl font-heading font-bold text-amber-600">
                                        <Coins className="w-6 h-6" />{formatCoins(student.balance || 0)}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Cantidad a Ajustar</Label>
                                      <div className="flex items-center gap-2">
                                        <Button type="button" variant="outline" size="icon" onClick={() => setAdjustAmount(prev => prev - 10)}><Minus className="w-4 h-4" /></Button>
                                        <Input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)} className="text-center text-lg font-bold" data-testid="adjust-amount" />
                                        <Button type="button" variant="outline" size="icon" onClick={() => setAdjustAmount(prev => prev + 10)}><Plus className="w-4 h-4" /></Button>
                                      </div>
                                      <p className="text-sm text-slate-500 text-center">Nuevo balance: {formatCoins((student.balance || 0) + adjustAmount)}</p>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Motivo del Ajuste *</Label>
                                      <Textarea placeholder="Explica el motivo del ajuste..." value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} required data-testid="adjust-note" />
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button onClick={handleAdjustBalance} disabled={submitting} data-testid="confirm-adjust">
                                      {submitting ? 'Ajustando...' : 'Confirmar Ajuste'}
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                              <Button size="sm" variant="outline" className="h-8 px-2 sm:px-3 text-red-600 hover:bg-red-50 border-red-200" onClick={() => handleRemoveStudent(student)} data-testid={`remove-student-${student.user_id}`}>
                                <UserMinus className="w-4 h-4" /><span className="hidden sm:inline ml-1">Quitar</span>
                              </Button>
                              {isOwner && !assistantIdSet.has(student.user_id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-2 sm:px-3 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  onClick={() => handlePromoteToAssistant(student)}
                                  disabled={promotingId === student.user_id}
                                  data-testid={`make-assistant-${student.user_id}`}
                                >
                                  <ShieldPlus className="w-4 h-4" />
                                  <span className="hidden sm:inline ml-1">
                                    {promotingId === student.user_id ? 'Asignando...' : 'Hacer Asistente'}
                                  </span>
                                </Button>
                              )}
                              {isOwner && assistantIdSet.has(student.user_id) && (
                                <span className="text-[10px] font-bold px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1" data-testid={`is-assistant-badge-${student.user_id}`}>
                                  <ShieldCheck className="w-3 h-3" />
                                  Asistente
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups">
          {/* Groups Summary */}
          {groups.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Card className="border-slate-200">
                <CardContent className="p-3 text-center">
                  <UsersRound className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-slate-900">{groups.length}</p>
                  <p className="text-xs text-slate-500">Grupos</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-3 text-center">
                  <Users className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-slate-900">{groups.reduce((s, g) => s + g.member_count, 0)}</p>
                  <p className="text-xs text-slate-500">Miembros Total</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-3 text-center">
                  <Wallet className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-600">{formatCoins(groups.reduce((s, g) => s + (g.group_wallet_balance || 0), 0))}</p>
                  <p className="text-xs text-slate-500">Coins Grupales</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-3 text-center">
                  <Zap className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                  <p className="text-xl font-bold text-amber-600">{groups.reduce((s, g) => s + g.total_xp, 0)}</p>
                  <p className="text-xs text-slate-500">XP Total</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-lg sm:text-xl">Grupos de Estudiantes</CardTitle>
                  <CardDescription>Organiza estudiantes en grupos para actividades colaborativas</CardDescription>
                </div>
                <Button onClick={handleOpenCreateGroup} data-testid="create-group-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Grupo</Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
              ) : groups.length === 0 ? (
                <div className="text-center py-12">
                  <UsersRound className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">No hay grupos creados</p>
                  <p className="text-sm text-slate-400">Crea grupos para organizar actividades colaborativas</p>
                  <Button onClick={handleOpenCreateGroup} className="mt-4"><Plus className="w-4 h-4 mr-2" />Crear Primer Grupo</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {groups.map(group => (
                    <div key={group.id} className="bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100/50 transition-colors" data-testid={`group-card-${group.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 cursor-pointer" onClick={() => setExpandedGroupId(expandedGroupId === group.id ? null : group.id)}>
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${group.color}20` }}>
                            <UsersRound className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: group.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium text-sm sm:text-base text-slate-900">{group.name}</h4>
                              <Badge variant="outline" className="text-xs">{group.member_count} {group.member_count === 1 ? 'miembro' : 'miembros'}</Badge>
                              {group.leader_name && (
                                <Badge className="bg-amber-50 text-amber-700 text-xs flex items-center gap-1"><Crown className="w-3 h-3" />{group.leader_name}</Badge>
                              )}
                            </div>
                            {group.description && <p className="text-xs sm:text-sm text-slate-500 truncate">{stripHtml(group.description)}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 ml-13 sm:ml-0">
                          <div className="text-right hidden sm:block">
                            <div className="flex items-center gap-1 text-blue-600"><Wallet className="w-4 h-4" /><span className="font-medium">{formatCoins(group.group_wallet_balance || 0)}</span></div>
                            <div className="flex items-center gap-1 text-purple-600 text-sm"><Zap className="w-3 h-3" /><span>{group.total_xp} XP</span></div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <Button size="sm" variant="outline" className="h-8 text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={(e) => { e.stopPropagation(); setGroupDetailId(group.id); setGroupDetailOpen(true); }} data-testid={`history-group-${group.id}`} title="Historial"><History className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8" onClick={(e) => handleOpenChat(group, e)} data-testid={`chat-group-${group.id}`} title="Chat"><MessageCircle className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8" onClick={(e) => handleOpenManageMembers(group, e)} data-testid={`manage-members-${group.id}`} title="Miembros"><UserPlus className="w-4 h-4" /></Button>
                            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8" onClick={(e) => handleOpenAwardGroup(group, e)} disabled={group.member_count === 0} data-testid={`award-group-${group.id}`} title="Otorgar"><Award className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8" onClick={(e) => handleOpenEditGroup(group, e)} data-testid={`edit-group-${group.id}`} title="Editar"><Pencil className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8 text-red-600 hover:bg-red-50" onClick={(e) => handleDeleteGroup(group.id, e)} data-testid={`delete-group-${group.id}`} title="Eliminar"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                          {expandedGroupId === group.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                      
                      {/* Expanded Group Detail */}
                      {expandedGroupId === group.id && group.members && (
                        <div className="border-t border-slate-200 p-3 sm:p-4 space-y-3">
                          {/* Group Stats */}
                          <div className="grid grid-cols-3 gap-2">
                            <div className="p-2 bg-blue-50 rounded-lg text-center">
                              <Wallet className="w-4 h-4 text-blue-500 mx-auto mb-0.5" />
                              <p className="text-lg font-bold text-blue-700">{formatCoins(group.group_wallet_balance || 0)}</p>
                              <p className="text-[10px] text-blue-600">Wallet Grupal</p>
                            </div>
                            <div className="p-2 bg-amber-50 rounded-lg text-center">
                              <Coins className="w-4 h-4 text-amber-500 mx-auto mb-0.5" />
                              <p className="text-lg font-bold text-amber-700">{formatCoins(group.total_coins)}</p>
                              <p className="text-[10px] text-amber-600">Coins Individuales</p>
                            </div>
                            <div className="p-2 bg-purple-50 rounded-lg text-center">
                              <Zap className="w-4 h-4 text-purple-500 mx-auto mb-0.5" />
                              <p className="text-lg font-bold text-purple-700">{group.total_xp}</p>
                              <p className="text-[10px] text-purple-600">XP Total</p>
                            </div>
                          </div>
                          
                          {/* Member List with Stats */}
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-2">Miembros ({group.members.length})</p>
                            <div className="space-y-1.5">
                              {group.members.map(member => (
                                <div key={member.user_id} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-slate-100">
                                  <div className={`w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 border-2 ${member.is_leader ? 'border-amber-400' : 'border-transparent'}`}>
                                    {member.photo_url ? (
                                      <img src={member.photo_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-xs font-medium text-slate-600">{member.user_name?.charAt(0).toUpperCase()}</span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <p className="font-medium text-sm truncate">{member.user_name}</p>
                                      {member.is_leader && <Crown className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                                    </div>
                                    <p className="text-xs text-slate-400 truncate">{member.user_email}</p>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs flex-shrink-0">
                                    <div className="flex items-center gap-1 text-amber-600" title="Coins individuales">
                                      <Coins className="w-3 h-3" />
                                      <span className="font-medium">{formatCoins(member.individual_coins || 0)}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-purple-600" title="XP individual">
                                      <Zap className="w-3 h-3" />
                                      <span className="font-medium">{member.individual_xp || 0}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Extracted Modals */}
      <GroupFormModal
        open={groupDialogOpen} onClose={() => { setGroupDialogOpen(false); resetGroupForm(); }}
        editingGroup={editingGroup} groupFormData={groupFormData} setGroupFormData={setGroupFormData}
        selectedStudents={selectedStudentsForGroup} toggleStudent={toggleStudentForGroup}
        students={students} submitting={submitting} onSubmit={handleSubmitGroup}
      />
      <GroupMembersModal
        open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)}
        group={selectedGroup} students={students} membersToManage={membersToManage}
        toggleMember={toggleMemberSelection} submitting={submitting} onSave={handleSaveMembers}
        classId={classId}
      />
      <GroupAwardModal
        open={awardGroupDialogOpen} onClose={() => setAwardGroupDialogOpen(false)}
        group={selectedGroup} activities={activeActivities}
        selectedActivity={groupAwardActivity} setSelectedActivity={setGroupAwardActivity}
        note={groupAwardNote} setNote={setGroupAwardNote}
        submitting={submitting} onAward={handleAwardGroup}
      />
      <StudentProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} classId={classId} student={profileStudent} />
      <GroupChatDialog open={chatDialogOpen} onClose={() => setChatDialogOpen(false)} classId={classId} group={chatGroup} currentUser={user} isProfessor={true} />
      <DirectChatDialog
        open={directChatOpen}
        onClose={() => setDirectChatOpen(false)}
        classId={classId}
        otherUser={directChatStudent}
        currentUser={user}
      />
      <GroupDetailDialog
        open={groupDetailOpen}
        onClose={() => { setGroupDetailOpen(false); setGroupDetailId(null); }}
        classId={classId}
        groupId={groupDetailId}
      />
      {isOwner && (
        <AssistantsManagerDialog
          open={assistantsDialogOpen}
          onOpenChange={setAssistantsDialogOpen}
          classId={classId}
          className={className}
          onChange={() => { loadData(); if (onRefresh) onRefresh(); }}
        />
      )}
    </div>
  );
}
